import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text, Modal, Spacing } from '@reservamos/elements';
import SnackPicker from 'ui/atoms/SnackPicker';
import ActionFooter from 'ui/atoms/ActionFooter';
import snackPlaceholder from 'images/snack-placeholder.webp';
import { formatCurrency } from '../../utils/currency';
import useCurrencyConfig from '../../hooks/whitelabel/useCurrencyConfig';

/**
 * SnacksModal component.
 * Pure presentational modal for displaying and selecting snacks.
 * Receives all data and callbacks as props, contains no business logic.
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible.
 * @param {function} props.onCloseModal - Callback when modal closes.
 * @param {Array} props.products - Full catalog with quantity field indicating selected quantity.
 * @param {function} props.onUpdateQuantity - Callback when quantity changes.
 * @param {number} props.totalPrice - Total price of selected products.
 * @param {number} props.totalQuantity - Total quantity of selected products.
 * @param {function} props.onActionButtonClick - Callback when action button is clicked.
 * @param {boolean} props.shouldShowBothTabs - Whether to display tabs for segments.
 * @param {string} props.activeSegment - Active segment ('outgoing' or 'incoming').
 * @param {function} props.onSegmentChange - Callback when segment tab changes.
 */
const SnacksModal = ({
  isOpen = false,
  onCloseModal,
  products = [],
  onUpdateQuantity,
  totalPrice = 0,
  totalQuantity = 0,
  onActionButtonClick,
  shouldShowBothTabs = false,
  activeSegment = 'outgoing',
  onSegmentChange,
}) => {
  const { t } = useTranslation('general');
  const { currency, locale, decimals } = useCurrencyConfig();

  if (!isOpen) {
    return null;
  }

  // Determine label and button text based on selection
  const label =
    totalQuantity === 0
      ? `${t('snacks_modal.add_something')} 😋`
      : formatCurrency(totalPrice, decimals, currency, locale);
  const buttonText =
    totalQuantity === 0 ? t('snacks_modal.no_thanks') : t('snacks_modal.add_to_trip');

  return (
    <Modal
      hasFooterShadow
      title={t('snacks_modal.title')}
      onCloseModal={onCloseModal}
      footer={
        <ActionFooter
          label={label}
          itemCount={totalQuantity}
          buttonText={buttonText}
          onButtonClick={onActionButtonClick}
        />
      }
    >
      {shouldShowBothTabs && (
        <div className="mt-1 mb-3">
          <div className="flex gap-3 bg-gray-100 rounded-full">
            <button
              type="button"
              onClick={() => onSegmentChange('outgoing')}
              className={`flex-1 px-4 py-2 font-medium rounded-full transition-all text-center ${
                activeSegment === 'outgoing'
                  ? 'bg-gradient-primary text-white shadow-md'
                  : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('snacks_modal.outgoing_trip')}
            </button>
            <button
              type="button"
              onClick={() => onSegmentChange('incoming')}
              className={`flex-1 px-4 py-2 font-medium rounded-full transition-all text-center ${
                activeSegment === 'incoming'
                  ? 'bg-gradient-primary text-white shadow-md'
                  : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('snacks_modal.incoming_trip')}
            </button>
          </div>
        </div>
      )}

      <Spacing size="L" vertical>
        <Text size="S">{t('snacks_modal.description')}</Text>
        <Spacing size="M" vertical>
          {products.length === 0 && (
            <div className="flex flex-col items-center bg-gray-100 rounded-xl py-8 gap-3">
              <img
                src={snackPlaceholder}
                alt="snack placeholder"
                className="w-16 h-16 opacity-50"
              />
              <Text size="M">{t('snacks_modal.no_products')}</Text>
            </div>
          )}
          {products.map((product) => {
            const isOutOfStock = product.stock === 0 || product.stock === undefined;

            return (
              <SnackPicker
                key={product.code}
                name={product.name}
                description={product.description}
                price={product.price}
                image={product.image}
                quantity={product.quantity}
                onQuantityChange={(quantity) => onUpdateQuantity(product.code, quantity)}
                isIncreaseDisabled={product.isIncreaseDisabled}
                isDecreaseDisabled={product.isDecreaseDisabled}
                outOfStock={isOutOfStock}
              />
            );
          })}
        </Spacing>
      </Spacing>
    </Modal>
  );
};

SnacksModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      price: PropTypes.number.isRequired,
      image: PropTypes.string,
      quantity: PropTypes.number.isRequired,
      stock: PropTypes.number,
    }),
  ),
  onUpdateQuantity: PropTypes.func.isRequired,
  totalPrice: PropTypes.number,
  totalQuantity: PropTypes.number,
  onActionButtonClick: PropTypes.func,
  shouldShowBothTabs: PropTypes.bool,
  activeSegment: PropTypes.string,
  onSegmentChange: PropTypes.func,
};

export default SnacksModal;
